import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '@/contexts/NotificationContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Bell,
  BellOff,
  Calendar,
  MessageSquare,
  DollarSign,
  Star,
  AlertTriangle,
  Check,
  CheckCheck,
  Trash2,
  Settings,
  ExternalLink
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { ar, enUS } from 'date-fns/locale';

const NotificationBell = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const lang = i18n.language;
  const locale = lang === 'ar' ? ar : enUS;
  
  const {
    notifications,
    unreadCount,
    notificationsLoading,
    permission,
    isSupported,
    refreshNotifications,
    requestPermission,
    markAsRead,
    markAllAsRead,
    clearNotifications
  } = useNotifications();
  
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (isOpen) {
      refreshNotifications({
        limit: Math.max(notifications.length, 20),
        offset: 0,
      });
    }
  }, [isOpen, notifications.length, refreshNotifications]);

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'appointment':
        return <Calendar className="h-4 w-4 text-blue-500" />;
      case 'message':
        return <MessageSquare className="h-4 w-4 text-green-500" />;
      case 'payment':
        return <DollarSign className="h-4 w-4 text-emerald-500" />;
      case 'review':
        return <Star className="h-4 w-4 text-amber-500" />;
      case 'alert':
        return <AlertTriangle className="h-4 w-4 text-red-500" />;
      default:
        return <Bell className="h-4 w-4 text-slate-500" />;
    }
  };

  const handleNotificationClick = (notification) => {
    markAsRead(notification.id);
    setIsOpen(false);
    
    if (notification.data?.url) {
      navigate(notification.data.url);
    }
  };

  const handleEnableNotifications = async () => {
    await requestPermission();
  };

  // Show enable button if notifications not granted
  if (permission !== 'granted' && isSupported) {
    return (
      <Button
        variant="outline"
        size="icon"
        onClick={handleEnableNotifications}
        className="relative"
        title={lang === 'ar' ? 'تفعيل الإشعارات' : 'Enable Notifications'}
        data-testid="enable-notifications-btn"
      >
        <BellOff className="h-5 w-5 text-slate-400" />
      </Button>
    );
  }

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          className="relative"
          data-testid="notification-bell-btn"
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <Badge 
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 bg-red-500 text-white text-xs"
              data-testid="notification-count"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      
      <DropdownMenuContent 
        align="end" 
        className="w-80 dark:bg-slate-800"
        data-testid="notification-dropdown"
      >
        <DropdownMenuLabel className="flex items-center justify-between">
          <span className="flex items-center gap-2">
            <Bell className="h-4 w-4" />
            {lang === 'ar' ? 'الإشعارات' : 'Notifications'}
          </span>
          {notifications.length > 0 && (
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={(e) => {
                  e.stopPropagation();
                  markAllAsRead();
                }}
                title={lang === 'ar' ? 'تحديد الكل كمقروء' : 'Mark all as read'}
              >
                <CheckCheck className="h-3 w-3" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 text-red-500 hover:text-red-600"
                onClick={(e) => {
                  e.stopPropagation();
                  clearNotifications();
                }}
                title={lang === 'ar' ? 'مسح الكل' : 'Clear all'}
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          )}
        </DropdownMenuLabel>
        
        <DropdownMenuSeparator />
        
        <ScrollArea className="h-[300px]">
          {notificationsLoading ? (
            <div className="flex flex-col items-center justify-center h-[200px] text-slate-500">
              <Bell className="h-10 w-10 mb-3 opacity-30 animate-pulse" />
              <p className="text-sm">
                {lang === 'ar' ? '\u062c\u0627\u0631\u064a \u062a\u062d\u0645\u064a\u0644 \u0627\u0644\u0625\u0634\u0639\u0627\u0631\u0627\u062a' : 'Loading notifications...'}
              </p>
            </div>
          ) : notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-[200px] text-slate-500">
              <Bell className="h-10 w-10 mb-3 opacity-30" />
              <p className="text-sm">
                {lang === 'ar' ? 'لا توجد إشعارات' : 'No notifications'}
              </p>
            </div>
          ) : (
            notifications.map((notification) => (
              <DropdownMenuItem
                key={notification.id}
                className={`flex items-start gap-3 p-3 cursor-pointer ${
                  !notification.read ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                }`}
                onClick={() => handleNotificationClick(notification)}
                data-testid={`notification-item-${notification.id}`}
              >
                <div className="flex-shrink-0 mt-1">
                  {getNotificationIcon(notification.type)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium truncate ${
                    !notification.read ? 'text-slate-900 dark:text-white' : 'text-slate-600 dark:text-slate-300'
                  }`}>
                    {notification.title}
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 line-clamp-2">
                    {notification.body}
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    {formatDistanceToNow(new Date(notification.timestamp), {
                      addSuffix: true,
                      locale
                    })}
                  </p>
                </div>
                {!notification.read && (
                  <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 mt-2" />
                )}
              </DropdownMenuItem>
            ))
          )}
        </ScrollArea>
        
        <DropdownMenuSeparator />
        
        <DropdownMenuItem
          className="flex items-center justify-center gap-2 text-teal-600"
          onClick={() => {
            setIsOpen(false);
            navigate('/notifications');
          }}
        >
          <ExternalLink className="h-4 w-4" />
          {lang === 'ar' ? 'عرض جميع الإشعارات' : 'View all notifications'}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default NotificationBell;
